// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-02:
// Student journey cluster article page: the free legal research stack.
// Fully public. Content lives in the data module.

import React from "react";
import { RelatedVideoLessons } from "@/components/resources/RelatedVideoLessons";
import Head from "next/head";
import {
  freeLegalResearchToolsSections,
  freeLegalResearchToolsSources,
} from "@/content/resources/freeLegalResearchTools";
import type { ResourceSection } from "@/content/resources/types";
import {
  ResourceBreadcrumbs,
  ResourceCrossLinks,
  ResourceDisclaimer,
  ResourceHero,
  ResourceSoftCta,
  SectionSourceLinks,
  SourcesSection,
} from "@/components/resources/ResourcePrimitives";

const SITE = "https://www.durmah.ai";

function SectionBlock({ section }: { section: ResourceSection }) {
  return (
    <section id={section.id} className="mb-14 scroll-mt-32">
      <h2 className="mb-4 text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
        {section.heading}
      </h2>
      {section.paragraphs.map((p, i) => (
        <p key={i} className="mb-4 leading-relaxed text-gray-600 dark:text-slate-400">
          {p}
        </p>
      ))}
      {section.bullets && (
        <ul className="mb-4 space-y-3">
          {section.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 leading-relaxed text-gray-600 dark:text-slate-400">
              <span aria-hidden className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {section.callout && (
        <div className="mt-5 rounded-2xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/70 dark:bg-indigo-500/5 p-6">
          <p className="mb-1.5 text-sm font-black text-indigo-900 dark:text-indigo-300">
            {section.callout.title}
          </p>
          <p className="m-0 text-sm leading-relaxed text-indigo-900/80 dark:text-indigo-200/70">
            {section.callout.body}
          </p>
        </div>
      )}
      <SectionSourceLinks links={section.links} />
    </section>
  );
}

export default function FreeLegalResearchToolsPage() {
  const canonical = `${SITE}/resources/free-legal-research-tools`;
  const title = "Free Legal Research Tools for UK Law Students | Durmah";
  const description =
    "Use free UK legal research tools including Find Case Law, BAILII and legislation.gov.uk, and learn when university databases add value.";

  const articleSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Free Legal Research Tools for UK Law Students",
    description,
    url: canonical,
    isAccessibleForFree: true,
    author: { "@type": "Organization", name: "Durmah" },
    publisher: {
      "@type": "Organization",
      name: "Durmah",
      logo: { "@type": "ImageObject", url: `${SITE}/android-chrome-512x512.png` },
    },
    datePublished: "2026-07-10",
    dateModified: "2026-07-10",
  });

  const breadcrumbSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Student Resources", item: `${SITE}/resources` },
      { "@type": "ListItem", position: 2, name: "Free Legal Research Tools", item: canonical },
    ],
  });

  return (
    <div className="bg-white dark:bg-[#020617] min-h-screen flex flex-col pt-32">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link key="route-canonical" rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta key="route-og-url" property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleSchema }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
      </Head>

      <main className="flex-1 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ResourceBreadcrumbs current="Free Legal Research Tools" />
          <ResourceHero
            eyebrow="Student journey"
            title={
              <>
                Legal Research,{" "}
                <span className="text-indigo-600 dark:text-indigo-400">Free</span>
              </>
            }
            subtitle="Find free judgments and legislative texts using Find Case Law, BAILII, legislation.gov.uk and the Supreme Court's own site. Coverage varies by court, date and jurisdiction; your university library provides further reports, commentary and research tools."
          />

          {/* In-page navigation */}
          <nav aria-label="On this page" className="mb-12 rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40 p-6">
            <p className="mb-3 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-slate-500">
              On this page
            </p>
            <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {freeLegalResearchToolsSections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm font-medium text-indigo-700 dark:text-indigo-400 hover:underline"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {freeLegalResearchToolsSections.map((section) => (
            <SectionBlock key={section.id} section={section} />
          ))}

          <RelatedVideoLessons
            heading="Put the research method into practice"
            intro="Watch three short lessons on finding free sources, checking case treatment and reading statutory provisions. Each includes a transcript and written study notes."
            slugs={["free-uk-legal-research-sources", "check-if-a-case-is-still-good-law", "read-a-uk-statute-four-passes", "case-found-check-the-claim", "statute-commencement-extent-application", "compare-competing-authorities"]}
          />
          <ResourceCrossLinks
            items={[
              {
                title: "How to Read a Case",
                description:
                  "What to do with the judgments you find: anatomy of a report, the ratio, and a briefing method.",
                href: "/resources/how-to-read-a-case",
              },
              {
                title: "OSCOLA Referencing Basics",
                description:
                  "Citing what your research finds: neutral citations, footnotes and the mistakes markers see.",
                href: "/resources/oscola-referencing-basics",
              },
              {
                title: "Law Dissertation Planning",
                description:
                  "The project this research stack was built for: a year of self-directed legal research, planned properly.",
                href: "/resources/law-dissertation-planning",
              },
            ]}
          />

          <SourcesSection groups={freeLegalResearchToolsSources} />
          <ResourceSoftCta
            title="Want a study companion that respects sources the way you now do?"
            body="Durmah can support your understanding of the sources you find. AI explanations and citations can be mistaken or incomplete: open the original authority, check its jurisdiction and current status, and follow your university's assessment rules. The research and final judgment stay yours. This guide stays free either way."
          />
          <ResourceDisclaimer />
        </div>
      </main>
    </div>
  );
}
